import React from 'react';
import { connect } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, Grid, DialogActions } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import DialogCloseButton from '../../../buttons/DialogCloseButton';
import UploadMediaButton from './common/UploadMediaButton';
import BackgroundStockImagesSearch from './sources/background/stock-images/BackgroundStockImagesSearch';
import BackgroundStockImagesContainer from './sources/background/stock-images/BackgroundStockImagesContainer';
import ImageCropperActions from './common/ImageCropperActions';
import ImageCropper from './common/ImageCropper';
import { mediaPickerActions } from './store';
import MediaPickersConstants from './MediaPickersConstants';
import BackgroundPickerTabs from './tabs/background/BackgroundPickerTabs';
import { ASPECT_RATIO, MEDIA_USAGE_TYPE } from '../CreatePageConstants';
import { trackEvent, AnalyticsConstants } from '../../../../utilities/analyticsUtilities';
import BackgroundCatalogVideosContainer from './sources/background/catalog-videos/BackgroundCatalogVideosContainer';
import CatalogVideoPreviewActions from './common/CatalogVideoPreviewActions';
import CatalogVideoPlayer from './common/CatalogVideoPlayer';
import BackgroundStockVideosSearch from './sources/background/stock-videos/BackgroundStockVideosSearch';
import BackgroundStockVideosContainer from './sources/background/stock-videos/BackgroundStockVideosContainer';
import StockVideoPreviewActions from './common/StockVideoPreviewActions';
import BackgroundStockVideoPlayer from './sources/background/stock-videos/BackgroundStockVideoPlayer';
import BackgroundAssets from './tabs/background/BackgroundAssets';

const styles = theme => ({
    imagesLibrarySection: {
        width: '100%',
        padding: `${theme.spacing(3, 0, 0, 0)} !important`,
        marginLeft: -theme.spacing(6),
        marginRight: -theme.spacing(6)
    },
    imagesDialogContent: {
        overflowY: 'hidden',
        padding: theme.spacing(1, 2)
    }
});

class BackgroundMediaPickerDialog extends React.Component {
    render() {
        const { 
            classes, imageCropperVisible, imageCropperProcessing, backgroundMediaPickerOpen, backgroundPickerSelectedTab, 
            catalogVideoPreviewVisible, backgroundStockVideoPreviewVisible, catalogVideoPreviewProcessing, backgroundStockVideoPreviewProcessing, videoSettings
        } = this.props;

        const mainControlsHidden = imageCropperProcessing || imageCropperVisible || catalogVideoPreviewVisible || backgroundStockVideoPreviewVisible || catalogVideoPreviewProcessing || backgroundStockVideoPreviewProcessing;
        
        let cropperConfiguration = MediaPickersConstants.IMAGE_CROPPER_CONFIGURATION.BACKGROUND_LANDSCAPE;
        let recommendedSize = `${MediaPickersConstants.RECOMMENDED_IMAGE_SIZES.BACKGROUND.WIDTH} x ${MediaPickersConstants.RECOMMENDED_IMAGE_SIZES.BACKGROUND.HEIGHT}`;
        
        if (videoSettings.aspectRatio === ASPECT_RATIO.PORTRAIT) {
            cropperConfiguration = MediaPickersConstants.IMAGE_CROPPER_CONFIGURATION.BACKGROUND_PORTRAIT;
            recommendedSize = `${MediaPickersConstants.RECOMMENDED_IMAGE_SIZES.BACKGROUND.HEIGHT} x ${MediaPickersConstants.RECOMMENDED_IMAGE_SIZES.BACKGROUND.WIDTH}`;
        } 
        else if (videoSettings.aspectRatio === ASPECT_RATIO.SQUARE) {
            cropperConfiguration = MediaPickersConstants.IMAGE_CROPPER_CONFIGURATION.BACKGROUND_SQUARE;
            recommendedSize = `${MediaPickersConstants.RECOMMENDED_IMAGE_SIZES.BACKGROUND.HEIGHT} x ${MediaPickersConstants.RECOMMENDED_IMAGE_SIZES.BACKGROUND.HEIGHT}`;
        } 

        return (
            <Dialog
                open={backgroundMediaPickerOpen}
                onClose={this.onDialogClose}
                maxWidth='md'
                fullWidth={true}
                scroll='body'
                aria-labelledby='media-picker-dialog'
                TransitionProps={{
                    onEnter: this.onDialogEnter,
                    onExited: this.onDialogExit
                }}>
                <DialogTitle id='media-picker-dialog'>
                    Background
                    <DialogCloseButton onClick={this.onDialogClose} />
                </DialogTitle>
                <DialogContent className={classes.imagesDialogContent}>
                    <Grid
                        container
                        direction='column'
                        justifyContent='center'
                        alignItems='center'
                        spacing={4}
                    >
                        <Grid hidden={mainControlsHidden}>
                            <UploadMediaButton 
                                title='Upload Background'
                                recommendedSize={recommendedSize}
                                mediaFormat={'jpeg'}
                                mediaUsageType={MEDIA_USAGE_TYPE.BACKGROUND}
                                uploadMimeTypes={MediaPickersConstants.BACKGROUND_UPLOAD_MIME_TYPES}
                            />
                        </Grid>
                        <Grid  
                            hidden={mainControlsHidden} 
                            className={classes.imagesLibrarySection}
                        >
                            <Grid
                                container
                                direction='column'
                                spacing={0}
                            >
                                <Grid className={classes.imageCollectionControlsWrapper}>
                                    <Grid
                                        container
                                        direction='row'
                                        justifyContent='space-between'
                                        alignItems='center'
                                    >
                                        <BackgroundPickerTabs />
                                        <Grid style={{flexGrow: 1}}></Grid>
                                        {backgroundPickerSelectedTab === MediaPickersConstants.MEDIA_PICKER_TABS.IMAGES && <BackgroundStockImagesSearch />}
                                        {backgroundPickerSelectedTab === MediaPickersConstants.MEDIA_PICKER_TABS.VIDEOS && <BackgroundStockVideosSearch />}
                                    </Grid>
                                </Grid>

                                { this.renderTabContent(backgroundPickerSelectedTab) }
                            </Grid>
                        </Grid>
                        <Grid>
                            <ImageCropper cropperConfiguration={cropperConfiguration} />
                            <CatalogVideoPlayer />
                            <BackgroundStockVideoPlayer/>
                        </Grid>     
                    </Grid>
                </DialogContent>
                {imageCropperVisible &&
                    <DialogActions>
                        <ImageCropperActions />
                    </DialogActions>
                }
                {catalogVideoPreviewVisible &&
                    <DialogActions>
                        <CatalogVideoPreviewActions />
                    </DialogActions>
                }
                {backgroundStockVideoPreviewVisible &&
                    <DialogActions>
                        <StockVideoPreviewActions />
                    </DialogActions>
                }
            </Dialog>
        );
    }

    renderTabContent = (backgroundPickerSelectedTab) => {
        const { backgroundStockVideoTabVisible } = this.props;

        switch(backgroundPickerSelectedTab) {
            case MediaPickersConstants.MEDIA_PICKER_TABS.IMAGES:
                return <BackgroundStockImagesContainer />;
            case MediaPickersConstants.MEDIA_PICKER_TABS.VIDEOS:
             return backgroundStockVideoTabVisible ? <BackgroundStockVideosContainer/> : <BackgroundCatalogVideosContainer />;
            case MediaPickersConstants.MEDIA_PICKER_TABS.MY_ASSETS:
                return <BackgroundAssets/>;    
            default:
                return null;
        }
    }

    onDialogEnter = () => {
        const {
            loadDefaultBackgroundStockImages, loadUserBackgroundAssets,
            loadInitialBackgroundCatalogVideos, backgroundPickerSelectedTab
        } = this.props;

        // The dialog opens on My Assets now, so only the gallery actually on screen is fetched.
        if (backgroundPickerSelectedTab === MediaPickersConstants.MEDIA_PICKER_TABS.MY_ASSETS)
            loadUserBackgroundAssets(1, MediaPickersConstants.BACKGROUND_ASSETS_PER_PAGE);
        else if (backgroundPickerSelectedTab === MediaPickersConstants.MEDIA_PICKER_TABS.VIDEOS)
            loadInitialBackgroundCatalogVideos();
        else
            loadDefaultBackgroundStockImages();

        trackEvent('Opened Media Picker', {
            category: AnalyticsConstants.VIDEO_CATEGORY,
            type: 'background'
        });
    }

    onDialogExit = () => {
        //clearImagesLibrary();
        //if (this.croppie) this.croppie.destroy();
    }

    onDialogClose = () => {
        const { setProperty, hideImageCropper, hideCatalogVideoPreview } = this.props;
        setProperty('backgroundMediaPickerOpen', false);
        hideImageCropper();
        hideCatalogVideoPreview();
    }
}

const mapStateToProps = (state) => {
    const {
        videoSettings,
    } = state.createPreview.present;

    const { 
        imageCropperVisible,
        imageCropperProcessing,
        backgroundMediaPickerOpen,
        backgroundPickerSelectedTab,
        catalogVideoPreviewVisible,
        backgroundStockVideoPreviewVisible,
        backgroundStockVideoTabVisible,
        catalogVideoPreviewProcessing,
        backgroundStockVideoPreviewProcessing
    } = state.mediaPicker;

    return { 
        videoSettings,
        imageCropperVisible,
        imageCropperProcessing,
        backgroundMediaPickerOpen,
        backgroundPickerSelectedTab,
        catalogVideoPreviewVisible,
        backgroundStockVideoPreviewVisible,
        backgroundStockVideoTabVisible,
        catalogVideoPreviewProcessing,
        backgroundStockVideoPreviewProcessing
    };
}

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(mediaPickerActions.setProperty(key, value));
    const loadDefaultBackgroundStockImages = () => dispatch(mediaPickerActions.loadDefaultBackgroundStockImages());
    const loadUserBackgroundAssets = (pageNumber, itemsPerPage) => dispatch(mediaPickerActions.loadUserBackgroundAssets(pageNumber, itemsPerPage));
    const loadInitialBackgroundCatalogVideos = () => dispatch(mediaPickerActions.loadInitialBackgroundCatalogVideos());
    const hideImageCropper = () => dispatch(mediaPickerActions.hideImageCropper());
    const hideCatalogVideoPreview = () => dispatch(mediaPickerActions.hideCatalogVideoPreview());

    return { 
        setProperty,
        loadDefaultBackgroundStockImages,
        loadUserBackgroundAssets,
        loadInitialBackgroundCatalogVideos,
        hideCatalogVideoPreview,
        hideImageCropper
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(BackgroundMediaPickerDialog, styles));